import { useState } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Button,
  Box,
  Container,
  Typography,
  Link,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { Visibility, VisibilityOff, Person, Email, Business, Lock } from "@mui/icons-material"

// -------------------- KairosLogin (refined) --------------------
function KairosLogin({ onLogin }) {
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + "/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      })
      if (response.ok) {
        const data = await response.json()
        localStorage.setItem("token", data.token)
        onLogin(data.token)
      } else {
        alert("Invalid credentials. Please try again.")
      }
    } catch (error) {
      console.error("Error during Kairos login:", error)
      alert("An unexpected error occurred. Please try again later.")
    }
  }

  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 2 }}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: "center" }}>
          Kairos User Login
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            fullWidth
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="toggle password visibility" onClick={toggleShowPassword} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            type="submit"
            sx={{
              mt: 1,
              py: 1.2,
              borderRadius: 2,
              background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
              boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
              "&:hover": {
                background: "linear-gradient(45deg, #1976D2 30%, #00B0FF 90%)",
              },
            }}
          >
            Sign In
          </Button>
        </Box>
      </Paper>
    </Container>
  )
}

// -------------------- RegularSignupForm --------------------
// Dentro de RegularSignupForm:
function RegularSignupForm({ toggleAuthMode, onLoginSuccess }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [organization, setOrganization] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInfoMessage(""); // Limpiar mensaje previo
    try {
      const signupResponse = await fetch(
        process.env.REACT_APP_API_URL + "/auth/signup_no_kairos",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, email, organization, password }),
        }
      );
      if (signupResponse.ok) {
        const signupData = await signupResponse.json();
        if (signupData.token) {
          localStorage.setItem("token", signupData.token);
          onLoginSuccess("regular", signupData.token);
        } else {
          const loginResponse = await fetch(
            process.env.REACT_APP_API_URL + "/auth/login_no_kairos",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email, password }),
            }
          );
          if (loginResponse.ok) {
            const loginData = await loginResponse.json();
            localStorage.setItem("token", loginData.token);
            onLoginSuccess("regular", loginData.token);
          } else {
            const loginError = await loginResponse.json();
            setInfoMessage(loginError.message || "Login failed. Please try again.");
          }
        }
      } else {
        const errorData = await signupResponse.json();
        setInfoMessage(errorData.message || "Sign-up failed. Please try again.");
      }
    } catch (error) {
      console.error("Error during signup:", error);
      setInfoMessage("An unexpected error occurred. Please try again later.");
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}
    >
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        fullWidth
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Person color="action" />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        fullWidth
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email color="action" />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        label="Organization"
        value={organization}
        onChange={(e) => setOrganization(e.target.value)}
        required
        fullWidth
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Business color="action" />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        label="Password"
        type={showPassword ? "text" : "password"}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        fullWidth
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={toggleShowPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button
        variant="contained"
        type="submit"
        sx={{
          mt: 1,
          py: 1.2,
          borderRadius: 2,
          background: "linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)",
          boxShadow: "0 3px 5px 2px rgba(139, 195, 74, .3)",
          "&:hover": {
            background: "linear-gradient(45deg, #388E3C 30%, #7CB342 90%)",
          },
        }}
      >
        Sign Up
      </Button>

      {infoMessage && (
        <Typography
          color="primary"
          sx={{
            mt: 1,
            p: 1,
            bgcolor: "rgba(33, 150, 243, 0.1)",
            borderRadius: 1,
            textAlign: "center",
          }}
        >
          {infoMessage}
        </Typography>
      )}

      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Typography variant="body2">
          Already have an account?{" "}
          <Link
            onClick={toggleAuthMode}
            component="button"
            sx={{
              fontWeight: "medium",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Log In
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}


// -------------------- RegularLoginForm --------------------
function RegularLoginForm({ onLogin, toggleAuthMode }) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + "/auth/login_no_kairos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      })
      if (response.ok) {
        const data = await response.json()
        localStorage.setItem("token", data.token)
        onLogin(data.token) // This callback should also close the modal in your parent component.
      } else {
        const errorData = await response.json()
        alert(errorData.message || "Login failed. Please try again.")
      }
    } catch (error) {
      console.error("Error during regular login:", error)
      alert("An unexpected error occurred. Please try again later.")
    }
  }

  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        fullWidth
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email color="action" />
            </InputAdornment>
          ),
        }}
      />
      <TextField
        label="Password"
        type={showPassword ? "text" : "password"}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        fullWidth
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label="toggle password visibility" onClick={toggleShowPassword} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Button
        variant="contained"
        type="submit"
        sx={{
          mt: 1,
          py: 1.2,
          borderRadius: 2,
          background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
          boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
          "&:hover": {
            background: "linear-gradient(45deg, #1976D2 30%, #00B0FF 90%)",
          },
        }}
      >
        Log In
      </Button>
      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Typography variant="body2">
          Don't have an account?{" "}
          <Link
            onClick={toggleAuthMode}
            component="button"
            sx={{
              fontWeight: "medium",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Sign Up
          </Link>
        </Typography>
      </Box>
    </Box>
  )
}

// -------------------- RegularAuth Component --------------------
function RegularAuth({ onLoginSuccess }) {
  const [isSignupMode, setIsSignupMode] = useState(true) //mostrar singup o login

  const toggleAuthMode = () => {
    setIsSignupMode(!isSignupMode)
  }

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: "center" }}>
        {isSignupMode ? "Create an Account" : "Welcome Back"}
      </Typography>
      {isSignupMode ? (
        <RegularSignupForm onLoginSuccess={onLoginSuccess} toggleAuthMode={toggleAuthMode} />
      ) : (
        <RegularLoginForm onLogin={onLoginSuccess} toggleAuthMode={toggleAuthMode} />
      )}
    </Paper>
  )
}

// -------------------- LoginModal Component --------------------
function LoginModal({ open, onClose, onLoginSuccess }) {
  // State to select between Kairos and regular user login.
  const [userTypeSelection, setUserTypeSelection] = useState("regular")
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const handleUserTypeChange = (e) => {
    setUserTypeSelection(e.target.value)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        },
      }}
    >

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, pt: 3 }}>
        <Box sx={{ width: "100%", mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              mb: 2,
              color: "#334155",
              fontWeight: 600,
              fontSize: "1rem",
            }}
          >
            Select your user type
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: { xs: 1, sm: 3 },
              flexDirection: { xs: "column", sm: "row" },
              width: "100%",
              maxWidth: "450px",
              mx: "auto",
            }}
          >
            <Box
              onClick={() => setUserTypeSelection("regular")}
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                border: "2px solid",
                borderColor: userTypeSelection === "regular" ? "primary.main" : "divider",
                bgcolor: userTypeSelection === "regular" ? "rgba(33, 150, 243, 0.08)" : "background.paper",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                boxShadow: userTypeSelection === "regular" ? "0 4px 12px rgba(33, 150, 243, 0.15)" : "none",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "rgba(33, 150, 243, 0.04)",
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: userTypeSelection === "regular" ? "primary.main" : "action.hover",
                  color: userTypeSelection === "regular" ? "white" : "text.secondary",
                  mb: 1,
                }}
              >
                <Person fontSize="medium" />
              </Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color={userTypeSelection === "regular" ? "primary.main" : "text.primary"}
              >
                User
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Standard account access
              </Typography>
            </Box>

            <Box
              onClick={() => setUserTypeSelection("kairos")}
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                border: "2px solid",
                borderColor: userTypeSelection === "kairos" ? "primary.main" : "divider",
                bgcolor: userTypeSelection === "kairos" ? "rgba(33, 150, 243, 0.08)" : "background.paper",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                boxShadow: userTypeSelection === "kairos" ? "0 4px 12px rgba(33, 150, 243, 0.15)" : "none",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "rgba(33, 150, 243, 0.04)",
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: userTypeSelection === "kairos" ? "primary.main" : "action.hover",
                  color: userTypeSelection === "kairos" ? "white" : "text.secondary",
                  mb: 1,
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color={userTypeSelection === "kairos" ? "primary.main" : "text.primary"}
              >
                Kairos User
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Organization member access
              </Typography>
            </Box>
          </Box>

          {/* Hidden radio group for accessibility and form submission */}
          <RadioGroup row value={userTypeSelection} onChange={handleUserTypeChange} sx={{ display: "none" }}>
            <FormControlLabel value="regular" control={<Radio />} label="Regular User" />
            <FormControlLabel value="kairos" control={<Radio />} label="Kairos User" />
          </RadioGroup>
        </Box>

        <Divider sx={{ mb: 4, mt: 1 }} />

        {userTypeSelection === "kairos" ? (
          <KairosLogin onLogin={(token) => onLoginSuccess("kairos", token)} />
        ) : (
          <RegularAuth onLoginSuccess={(token) => onLoginSuccess("regular", token)} />
        )}
      </DialogContent>
    </Dialog>
  )
}

export default LoginModal
